import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import usePublisher from 'api/usePublisher';

import { Separator } from 'components/ui/separator';

import { PREVIEW_CONSTS, previewExampleItems } from './preview.consts';

type ReceiptPreviewProps = {
  receiptLogo: string;
  receiptHeader: string;
  companyAddressStructured?: {
    line1: string;
    line2: string;
    line3: string;
  };
  addFooter?: boolean;
  termsOfServiceUrl?: string;
  privacyPolicyUrl?: string;
  supportUrl?: string;
};

const PreviewRow = ({
  label,
  value
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <Stack direction="row" justifyContent="space-between" width="100%">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Stack>
  );
};

export const ReceiptPreview = ({
  receiptLogo,
  receiptHeader,
  addFooter,
  companyAddressStructured,
  termsOfServiceUrl,
  privacyPolicyUrl,
  supportUrl
}: ReceiptPreviewProps) => {
  const [mode, setMode] = useState<'mobile' | 'desktop'>('mobile');
  const { publisherId } = useParams();
  const { getPublisher } = usePublisher(publisherId || undefined);

  const invoiceMerchant = getPublisher.data?.invoiceMerchant || '';

  const cardWidth = useMemo(() => (mode === 'mobile' ? 360 : 480), [mode]);

  return (
    <Stack alignItems="flex-end" width="100%">
      <Stack direction="row" gap={1} mb={1}>
        <IconButton
          aria-label="mobile"
          onClick={() => setMode('mobile')}
          color={mode === 'mobile' ? 'primary' : 'default'}
          size="small"
        >
          <SmartphoneOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="desktop"
          onClick={() => setMode('desktop')}
          color={mode === 'desktop' ? 'primary' : 'default'}
          size="small"
        >
          <ViewQuiltOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box
        sx={{
          width: cardWidth,
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
          backgroundColor: '#fff'
        }}
      >
        <Box
          sx={{
            backgroundColor: receiptHeader ? 'transparent' : '#2f2f35',
            height: 160,
            width: '100%',
            overflow: 'hidden'
          }}
        >
          {receiptHeader ? (
            <Box
              component="img"
              src={receiptHeader}
              alt="header"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
        </Box>
        <Box p={2}>
          <Typography fontWeight={700} mb={1.5} fontSize={16}>
            Purchase Confirmation
          </Typography>
          <Stack direction="row" alignItems="center" gap={1.5} mb={1.5}>
            <Box
              component="img"
              src={receiptLogo}
              alt="logo"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '4px',
                objectFit: 'contain'
              }}
            />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {invoiceMerchant}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <PreviewRow label="Receipt Number" value="602-758-041" />
            <PreviewRow label="Payment Method" value="Visa (1111)" />
            <PreviewRow
              label="Payment Date"
              value="November 13, 2025 3:21 PM UTC"
            />
            <PreviewRow label="Country" value="Israel" />
            <PreviewRow label="Order ID" value="6915f7714cd9f675fd17df84" />
          </Stack>
          <Separator className="my-6 bg-base-border" />
          <Typography variant="body2" fontWeight={700} gutterBottom>
            Items
          </Typography>
          <Stack gap={1.25}>
            {previewExampleItems.map((item) => (
              <Stack
                key={item.label}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Stack direction="row" alignItems="center" gap={1.25}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: item.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16
                    }}
                  >
                    {item.emoji}
                  </Box>
                  <Typography variant="body2">{item.label}</Typography>
                </Stack>
                <Typography variant="body2">{item.qty}</Typography>
              </Stack>
            ))}
          </Stack>
          <Separator className="my-6 bg-base-border" />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" fontWeight={700}>
              Subtotal
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.SUBTOTAL}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mt={0.75}
          >
            <Typography variant="body2" fontWeight={700}>
              Tax (18%)
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.TAX}
            </Typography>
          </Stack>
          <Separator className="my-6 bg-base-border" />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" fontWeight={700}>
              Total
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              ${PREVIEW_CONSTS.TOTAL}
            </Typography>
          </Stack>
          <Stack alignItems="center" mt={2} gap={0.5}>
            <Typography variant="caption" color="text.secondary">
              Powered by Appcharge
            </Typography>
            <Typography variant="caption" color="text.secondary">
              For questions or assistance, contact{' '}
              {supportUrl ? (
                <a
                  href={supportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 no-underline"
                >
                  Appcharge support
                </a>
              ) : (
                'Appcharge support'
              )}
            </Typography>
          </Stack>
          {addFooter ? (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              mt={2}
              gap={2}
            >
              <Stack gap={0.25}>
                {companyAddressStructured && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      {companyAddressStructured.line1}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {companyAddressStructured.line2}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {companyAddressStructured.line3}
                    </Typography>
                  </>
                )}
              </Stack>
              <Stack gap={1} alignItems="flex-end">
                <a
                  href={termsOfServiceUrl || 'https://www.appcharge.com/tc'}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 no-underline text-xs"
                >
                  Terms of Service
                </a>
                <a
                  href={privacyPolicyUrl || 'https://www.appcharge.com/privacy'}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 no-underline text-xs"
                >
                  Privacy Policy
                </a>
              </Stack>
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Stack>
  );
};

export default ReceiptPreview;
